/**
 * Lightweight Web Vitals → PostHog / GA4 (no extra dependency).
 * BFS §2.1 - LCP / CLS / INP when PerformanceObserver supports them.
 */

type VitalName = "LCP" | "CLS" | "INP" | "FCP" | "TTFB";

function report(name: VitalName, value: number, id: string) {
  const rounded =
    name === "CLS" ? Math.round(value * 1000) / 1000 : Math.round(value);
  try {
    window.__SG_POSTHOG__?.capture?.("web_vital", {
      product: "safegeo",
      name,
      value: rounded,
      metric_id: id,
    });
  } catch {
    /* ignore */
  }
  try {
    window.gtag?.("event", name, {
      event_category: "Web Vitals",
      value: rounded,
      metric_id: id,
      non_interaction: true,
    });
  } catch {
    /* ignore */
  }
}

function observe(
  type: string,
  onEntry: (entry: PerformanceEntry) => void,
): void {
  try {
    if (typeof PerformanceObserver === "undefined") return;
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) onEntry(entry);
    });
    po.observe({ type, buffered: true } as PerformanceObserverInit);
  } catch {
    /* unsupported type */
  }
}

/** Call once after monitoring vendors boot. */
export function startWebVitals(): void {
  if (typeof window === "undefined") return;

  observe("largest-contentful-paint", (entry) => {
    report("LCP", entry.startTime, entry.name || "lcp");
  });

  observe("first-contentful-paint", (entry) => {
    report("FCP", entry.startTime, "fcp");
  });

  let cls = 0;
  observe("layout-shift", (entry) => {
    const e = entry as PerformanceEntry & {
      hadRecentInput?: boolean;
      value?: number;
    };
    if (!e.hadRecentInput && typeof e.value === "number") {
      cls += e.value;
      report("CLS", cls, "cls");
    }
  });

  observe("event", (entry) => {
    const e = entry as PerformanceEntry & {
      duration?: number;
      interactionId?: number;
      name?: string;
    };
    if (e.name === "event" || typeof e.duration === "number") {
      const dur = e.duration ?? 0;
      if (dur > 0) report("INP", dur, `inp-${e.interactionId ?? 0}`);
    }
  });

  try {
    const nav = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming | undefined;
    if (nav?.responseStart) {
      report("TTFB", nav.responseStart, "ttfb");
    }
  } catch {
    /* ignore */
  }
}
